import React, { useEffect, useMemo, useState } from "react"
import { View, StyleProp, ViewStyle } from "react-native"
import { AdminViewDocument, AdminViewQuery, useInterruptDeliveryMutation } from "./queries.generated"
import { groupBy, toString } from "lodash-es"
import MainScrollPanel from "../../components/layout/MainScrollPanel"
import Base from "../../components/layout/Base"
import { ActiveViewerDocument } from "../ActiveItems/queries.generated"
import { useTranslation } from "react-i18next"
import { Button, ErrorMessage, Text, TouchButton, useTheme } from "pusatec-react-native"
import { BsArrowRight } from "react-icons/bs"
import { useNavigation } from "@react-navigation/core"
import { StackNavigationProp } from "@react-navigation/stack"
import { MainStackParamList } from "../../MainNavigation"
import { FormatRichHtml } from "../../components/FormatRichHtml"
import { AdminToolbar } from "./admin_toolbar"
import { HeaderBar } from "../../components/Headerbar"
import { formatDateTime } from "../../utils/date"
import { LocalDatetime } from "../../components/local-datetime"
import { useUpdateDeliveryMutation } from "../ViewDelivery/queries.generated"

interface Props {
    systemId: string
    deliveries: NonNullable<AdminViewQuery["lockersystem"]>["activeDeliveries"]["deliveries"]
    onRefetch: () => void
    loading: boolean
    style?: StyleProp<ViewStyle>
}

export function ManageDeliveries(props: Props) {
	const { systemId, deliveries, loading, onRefetch, style } = props
	const navigation = useNavigation<StackNavigationProp<MainStackParamList, "AdminNavigation">>()
	const { t } = useTranslation()
	const { colors } = useTheme()

	const notPlacedDeliveries = useMemo(() => deliveries.filter(delivery =>
		delivery.useDeliveryCodeBefore && !delivery.deliveryCodeUsedAt
	), [deliveries])

	const notPlacedDeliveryIds = useMemo(() => new Set(notPlacedDeliveries.map(delivery => delivery.id)), [notPlacedDeliveries])

	const deliveriesGroups = useMemo(() => groupBy(
		deliveries.filter(delivery => !notPlacedDeliveryIds.has(delivery.id)),
		i => i.locker?.lockerGroup?.name ?? t("Unassigned group")
	), [deliveries, notPlacedDeliveryIds, t])

	const deliverySections = useMemo(() => {
		const sections: Array<[string, Props["deliveries"]]> = []

		if (notPlacedDeliveries.length) {
			sections.push([t("Not placed yet"), notPlacedDeliveries])
		}

		sections.push(...Object.entries(deliveriesGroups) as Array<[string, Props["deliveries"]]>)

		return sections
	}, [deliveriesGroups, notPlacedDeliveries, t])

	const [selectedDeliveryId, setSelectedDeliveryId] = useState<string | null>(null)
	const [placementDeadline, setPlacementDeadline] = useState<string | null>(null)
	const [pickupBefore, setPickupBefore] = useState<string | null>(null)
	const [pickupBeforeDirty, setPickupBeforeDirty] = useState(false)

	const [interruptDelivery, interruptDeliveryM] = useInterruptDeliveryMutation({
		onCompleted: data => {
			navigation.push("InterruptDelivery", { deliveryId: data!.interruptDelivery.delivery!.id })
		}
	})

	const [updateDelivery, updateDeliveryM] = useUpdateDeliveryMutation()

	useEffect(() => {
		if (!selectedDeliveryId || !notPlacedDeliveryIds.has(selectedDeliveryId)) {
			setPlacementDeadline(null)
			return
		}

		const selected = notPlacedDeliveries.find(delivery => delivery.id === selectedDeliveryId)
		if (!selected) {
			setPlacementDeadline(null)
			return
		}

		const nextDeadline = selected.useDeliveryCodeBefore ?? new Date().toISOString()
		setPlacementDeadline(prev => prev === nextDeadline ? prev : nextDeadline)
	}, [selectedDeliveryId, notPlacedDeliveries, notPlacedDeliveryIds])

	useEffect(() => {
		if (!selectedDeliveryId) {
			setPickupBefore(null)
			setPickupBeforeDirty(false)
			return
		}

		const selected = deliveries.find(delivery => delivery.id === selectedDeliveryId)
		if (!selected) {
			setPickupBefore(null)
			setPickupBeforeDirty(false)
			return
		}

		const nextPickupBefore = selected.pickupBefore ?? new Date().toISOString()
		setPickupBefore(prev => prev === nextPickupBefore ? prev : nextPickupBefore)
		setPickupBeforeDirty(false)
	}, [deliveries, selectedDeliveryId])

	return <View style={[{ flex: 1 }, style]} >
		<MainScrollPanel
			style={{ borderWidth: 0, backgroundColor: undefined }}
			onRefresh={onRefetch}
			refreshing={loading}
		>
			{deliverySections.map(([key, deliveries]) => (
				<View key={key} style={{ marginBottom: 10 }}>
					<HeaderBar text={key}  />
					{deliveries.map(delivery => {
						const selected = delivery.id === selectedDeliveryId
						const serverPickupBefore = delivery.pickupBefore ?? null
						const pickupTimeRunOut = (serverPickupBefore && new Date(serverPickupBefore) < new Date())
						const whoCanPickup = delivery.whoCanPickup.length < 2 ?
							delivery.whoCanPickup :
							delivery.whoCanPickup.filter(whoCanPickup => delivery.userWhoDelivered?.id !== whoCanPickup.id)
						const isNotPlacedDelivery = notPlacedDeliveryIds.has(delivery.id)
						const whoCanPickupString = whoCanPickup.map(user => user.name || user.email || user.identityCode).join(", ")
						const showPlacementEditor = selected && isNotPlacedDelivery
						const showPickupBeforeEditor = selected && pickupBefore !== null
						const serverPlacementDeadline = delivery.useDeliveryCodeBefore ?? null
						const placementDeadlineChanged = showPlacementEditor && placementDeadline !== null && placementDeadline !== serverPlacementDeadline
						const pickupBeforeChanged = showPickupBeforeEditor && pickupBeforeDirty && pickupBefore !== serverPickupBefore

						return <Base
							key={delivery.id}
							onPress={() => setSelectedDeliveryId(delivery.id)}
							selected={delivery.id === selectedDeliveryId}
							style={{
								borderRightColor: pickupTimeRunOut ? colors.error : "rgba(0,0,0,0)",
								borderRightWidth: 8,
							}}
						>
							<View style={{ flexDirection: "row", alignItems: "center" }}>
								<Text text={toString(delivery.userWhoDelivered?.name || delivery.userWhoDelivered?.email)} bold />
								<BsArrowRight style={{ marginLeft: 5, marginRight: 5, flexShrink: 0 }} />
								<Text text={whoCanPickupString} bold />
							</View>
							<Text text={t("Locker {{name}}", { name: toString(delivery.locker?.name) })} />
							{isNotPlacedDelivery && delivery.useDeliveryCodeBefore && !showPlacementEditor
								? <Text text={t("Place in locker before {{time}}", { time: formatDateTime(delivery.useDeliveryCodeBefore) })} />
								: null}
							{pickupTimeRunOut
								? <Text
									text={t("Pickup time has run out")}
									style={{ color: colors.error }}
									bold
								/>
								: null}

							{selected ? <>
								<FormatRichHtml html={delivery.description} max={200} />
								{delivery.createdAt && <Text text={t("Created {{time}}", { time: formatDateTime(delivery.createdAt) })} />}
								{showPickupBeforeEditor && pickupBefore
									? <View style={{ marginTop: 10 }}>
										<Text text={t("Pickup before")} />
										<LocalDatetime
											style={{ width: "220px", height: "30px", marginTop: 6 }}
											min={new Date()}
											datetime={pickupBefore!}
											onChange={value => {
												setPickupBefore(value)
												setPickupBeforeDirty(value !== serverPickupBefore)
											}}
										/>
										{pickupBeforeChanged && <Button
											containerStyle={{ marginTop: 10, width: "140px" }}
											disabled={updateDeliveryM.loading}
											text={t("Update")}
											onPress={() => {
												if (!pickupBefore) {
													return
												}

												updateDelivery({
													variables: {
														input: {
															deliveryId: delivery.id,
															pickupBefore
														}
													},
													refetchQueries: [
														{ query: ActiveViewerDocument },
														{ query: AdminViewDocument, variables: { systemId } }
													]
												}).then(() => {
													setPickupBeforeDirty(false)
													if (onRefetch) {
														onRefetch()
													}
												}).catch(() => {
													// Error handled by ErrorMessage component
												})
											}}
										/>}
									</View>
									: serverPickupBefore
										? <Text text={t("Pickup before {{time}}", { time: formatDateTime(serverPickupBefore) })} />
										: null}
								{showPlacementEditor && placementDeadline ? <View style={{ marginTop: 10 }}>
									<Text text={t("Place in locker before")} />
									<LocalDatetime
										style={{ width: "220px", height: "30px", marginTop: 6 }}
										min={new Date()}
										datetime={placementDeadline}
										onChange={setPlacementDeadline}
									/>
									{placementDeadlineChanged && <Button
										containerStyle={{ marginTop: 10, width: "140px" }}
										disabled={updateDeliveryM.loading}
										text={t("Update")}
										onPress={() => {
											if (!placementDeadline) {
												return
											}

											updateDelivery({
												variables: {
													input: {
														deliveryId: delivery.id,
														useCodeBefore: placementDeadline
													}
												},
												refetchQueries: [
													{ query: ActiveViewerDocument },
													{ query: AdminViewDocument, variables: { systemId } }
												]
											}).then(() => {
												if (onRefetch) {
													onRefetch()
												}
											}).catch(() => {
												// Error handled by ErrorMessage component
											})
										}}
									/>}
								</View> : serverPlacementDeadline
									? (isNotPlacedDelivery
										? <Text text={t("Place in locker before {{time}}", { time: formatDateTime(serverPlacementDeadline) })} />
										: null)
									: null}
								{updateDeliveryM.error && <ErrorMessage extra={updateDeliveryM.error} />}
							</> : null}
						</Base>
					})}
				</View>
			))}
		</MainScrollPanel >
		<AdminToolbar>
			{interruptDeliveryM.error && <ErrorMessage extra={interruptDeliveryM.error} />}

			{selectedDeliveryId && <TouchButton
				text={t("Interrupt delivery")}
				containerStyle={{ justifyContent: "flex-end"}}
				loading={interruptDeliveryM.loading}
				onPress={() => {
					interruptDelivery({
						variables: { deliveryId: selectedDeliveryId },
						refetchQueries: [
							{
								query: ActiveViewerDocument,
							},
							{
								query: AdminViewDocument,
								variables: { systemId }
							}
						]
					})
				}}
			/>}
		</AdminToolbar>
	</View >
}
